/**
 * CompareQuotesDialog — operator picks 2-4 quotes from across the
 * appraisal's Restoration scenarios + Re-roof specs, then renders a
 * side-by-side comparison PDF.
 *
 * Mounted in 3 places (each opens the SAME dialog):
 *   • Restoration Builder tab header
 *   • Re-roof Builder tab header
 *   • Appraisal Overview header
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Loader2, FileText, ExternalLink, AlertCircle, Star } from 'lucide-react';

export default function CompareQuotesDialog({ api, appraisalId, open, onOpenChange, axiosBaseURL, authHeader }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [picked, setPicked] = useState({});      // id -> bool
  const [lifespans, setLifespans] = useState({}); // id -> number
  const [recommended, setRecommended] = useState(''); // id of recommended option (or '')
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    if (!open) return;
    setErr(null);
    try {
      const { data: d } = await api.get(`/appraisals/${appraisalId}/compare/options`);
      setData(d);
      // Initialise picker state with the preselect ids.
      const preselect = d.defaults?.preselect_ids || [];
      const p = {};
      const ls = {};
      d.options.forEach(o => {
        p[o.id] = preselect.includes(o.id);
        ls[o.id] = o.lifespan_yrs;
      });
      setPicked(p);
      setLifespans(ls);
      // Default recommended = the chosen re-roof if present, else the cheapest option.
      const reroofChosen = d.options.find(o => o.kind === 'reroof' && o.is_chosen);
      setRecommended(reroofChosen?.id || '');
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    }
  }, [api, appraisalId, open]);
  useEffect(() => { load(); }, [load]);

  const selectedCount = useMemo(() => Object.values(picked).filter(Boolean).length, [picked]);
  const cheapest = useMemo(() => {
    if (!data) return 0;
    const totals = data.options.filter(o => picked[o.id]).map(o => o.total);
    return totals.length ? Math.min(...totals) : 0;
  }, [data, picked]);

  const toggle = (id) => setPicked(p => ({ ...p, [id]: !p[id] }));
  const updateLifespan = (id, v) => setLifespans(ls => ({ ...ls, [id]: v }));

  const generate = async () => {
    setErr(null);
    setSubmitting(true);
    try {
      if (selectedCount < 2) { setErr('Pick at least 2 quotes to compare.'); setSubmitting(false); return; }
      if (selectedCount > 4) { setErr('Maximum 4 quotes per PDF.'); setSubmitting(false); return; }
      const selections = Object.entries(picked).filter(([, v]) => v).map(([id]) => ({
        id,
        lifespan_yrs: Number(lifespans[id]) || 0,
        recommended: id === recommended,
      }));
      const resp = await api.post(`/appraisals/${appraisalId}/compare/pdf`,
                                    { selections },
                                    { responseType: 'blob' });
      const blob = new Blob([resp.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || 'Failed to render PDF');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl" data-testid="compare-quotes-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><FileText size={16} /> Compare Quotes — side-by-side PDF</DialogTitle>
        </DialogHeader>
        {!data ? (
          <div className="text-xs text-slate-500 py-6 text-center"><Loader2 size={11} className="inline animate-spin mr-1" /> Loading available quotes…</div>
        ) : (
          <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
            <p className="text-xs text-slate-500">
              Pick <strong>2–4 quotes</strong> below. The PDF will show grand totals, Stage 1 / Stage 2 breakdowns,
              expected lifespan, cost-per-year, and a "what's included" tick-list for each.
            </p>
            {data.options.length === 0 ? (
              <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-3" data-testid="no-quotes">
                <AlertCircle size={12} className="inline mr-1" />
                No quotes available yet — set up at least one Restoration scenario or Re-roof spec first.
              </div>
            ) : (
              <table className="w-full text-xs" data-testid="compare-options-table">
                <thead className="text-slate-500 border-b border-slate-200">
                  <tr>
                    <th className="w-10"></th>
                    <th className="text-left py-1.5">Quote</th>
                    <th className="text-right">Grand total</th>
                    <th className="text-right pr-2">Δ vs cheapest</th>
                    <th className="text-right">Lifespan (yrs)</th>
                    <th className="text-center">Recommended</th>
                  </tr>
                </thead>
                <tbody>
                  {data.options.map(o => {
                    const isPicked = !!picked[o.id];
                    const delta = isPicked && cheapest > 0 ? o.total - cheapest : 0;
                    return (
                      <tr key={o.id} className={`border-b border-slate-100 last:border-0 ${isPicked ? 'bg-emerald-50/50' : ''}`}
                          data-testid={`compare-row-${o.id}`}>
                        <td className="py-1.5 text-center">
                          <input type="checkbox" checked={isPicked}
                                 onChange={() => toggle(o.id)}
                                 data-testid={`compare-check-${o.id}`}
                                 className="rounded text-emerald-600 focus:ring-emerald-400" />
                        </td>
                        <td>
                          <div className="font-medium text-slate-800">{o.label}</div>
                          {o.sublabel && <div className="text-[11px] text-slate-500 italic">{o.sublabel}</div>}
                        </td>
                        <td className="text-right font-mono-num font-semibold text-slate-900">${o.total.toLocaleString('en-NZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                        <td className="text-right font-mono-num pr-2 text-xs">
                          {isPicked
                            ? (delta > 0
                                ? <span className="text-amber-700">+${delta.toLocaleString('en-NZ', { maximumFractionDigits: 0 })}</span>
                                : <span className="text-emerald-700 font-semibold">cheapest</span>)
                            : <span className="text-slate-300">—</span>}
                        </td>
                        <td className="text-right">
                          <Input type="number" min="1" max="60" step="1"
                                 value={lifespans[o.id] ?? o.lifespan_yrs}
                                 disabled={!isPicked}
                                 data-testid={`compare-lifespan-${o.id}`}
                                 onChange={(e) => updateLifespan(o.id, parseFloat(e.target.value) || 0)}
                                 className="h-7 text-xs text-right w-20 inline-block font-mono-num" />
                        </td>
                        <td className="text-center">
                          <input type="radio" checked={recommended === o.id}
                                 disabled={!isPicked}
                                 onChange={() => setRecommended(o.id)}
                                 data-testid={`compare-recommended-${o.id}`}
                                 name="rec-radio"
                                 className="text-emerald-600 focus:ring-emerald-400" />
                          {recommended === o.id && <Star size={11} className="inline ml-1 text-emerald-600 fill-emerald-600" />}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
            <div className="text-xs text-slate-500 italic flex items-center justify-between">
              <span>Selected <strong className="text-slate-700">{selectedCount}</strong> of 2–4 quotes</span>
              <span>Lifespans are guidance only — operator can override per option.</span>
            </div>
            {err && <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded p-2" data-testid="compare-error"><AlertCircle size={12} className="inline mr-1" /> {err}</div>}
          </div>
        )}
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="compare-cancel">Cancel</Button>
          <Button onClick={generate}
                  disabled={submitting || selectedCount < 2 || selectedCount > 4}
                  data-testid="compare-generate-pdf"
                  className="bg-emerald-600 hover:bg-emerald-700 text-white">
            {submitting ? <Loader2 size={13} className="animate-spin mr-1" /> : <ExternalLink size={13} className="mr-1" />}
            Generate Comparison PDF
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
